import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { GitBranch, Plus, Edit, Copy, Trash2, AlertCircle, CheckCircle, Star, Eye, Power } from 'lucide-react';

const FlowsPage: React.FC = () => {
  const [flows, setFlows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<any>(null);
  const [duplicateName, setDuplicateName] = useState('');

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    try {
      setIsLoading(true);
      const response = await api.getFlows();
      setFlows(response.flows || []);
    } catch (error) {
      console.error('Error loading flows:', error);
      setError('Erro ao carregar fluxos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedFlow) return;

    try {
      await api.deleteFlow(selectedFlow.id);
      setSuccess(`Fluxo "${selectedFlow.name}" deletado com sucesso`);
      setShowDeleteModal(false);
      setSelectedFlow(null);
      loadFlows();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error deleting flow:', error);
      setError(error.response?.data?.error || 'Erro ao deletar fluxo');
      setShowDeleteModal(false);

      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedFlow || !duplicateName.trim()) return;

    try {
      await api.duplicateFlow(selectedFlow.id, duplicateName);
      setSuccess(`Fluxo duplicado como "${duplicateName}"`);
      setShowDuplicateModal(false);
      setSelectedFlow(null);
      setDuplicateName('');
      loadFlows();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error duplicating flow:', error);
      setError(error.response?.data?.error || 'Erro ao duplicar fluxo');

      setTimeout(() => setError(null), 5000);
    }
  };

  const openDuplicateModal = (flow: any) => {
    setSelectedFlow(flow);
    setDuplicateName(`${flow.name} (Cópia)`);
    setShowDuplicateModal(true);
  };

  const openDeleteModal = (flow: any) => {
    setSelectedFlow(flow);
    setShowDeleteModal(true);
  };

  const handleToggleActive = async (flow: any) => {
    if (flow.isDefault) {
      setError('Não é possível desativar o fluxo padrão');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      await api.updateFlow(flow.id, { isActive: !flow.isActive });
      setSuccess(`Fluxo "${flow.name}" ${!flow.isActive ? 'ativado' : 'desativado'} com sucesso`);
      loadFlows();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error toggling flow status:', error);
      setError(error.response?.data?.error || 'Erro ao alterar status do fluxo');
      setTimeout(() => setError(null), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <GitBranch className="w-6 h-6" />
              Fluxos de Atendimento
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Gerencie fluxos personalizados para disparos em massa
            </p>
          </div>
          <button
            onClick={() => {
              setIsNavigating(true);
              window.location.href = '/flows/new';
            }}
            disabled={isNavigating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isNavigating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Carregando...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Novo Fluxo
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Flows List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {isLoading ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              Carregando fluxos...
            </div>
          ) : flows.filter(flow => !flow.isDefault).length === 0 ? (
            <div className="p-12 text-center">
              <GitBranch className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-500 dark:text-gray-400 mb-2">Nenhum fluxo encontrado</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Clique em "Novo Fluxo" para criar seu primeiro fluxo personalizado
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="w-1/3 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="w-1/3 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="w-24 px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nós
                    </th>
                    <th className="w-32 px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {flows.filter(flow => !flow.isDefault).map((flow) => (
                    <tr key={flow.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {flow.name}
                          </span>
                          {flow.isDefault && (
                            <span title="Fluxo padrão">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="text-sm text-gray-600 dark:text-gray-400 truncate block"
                          title={flow.description || '-'}
                        >
                          {flow.description || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {flow._count?.nodes || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleToggleActive(flow)}
                            disabled={flow.isDefault}
                            className={`p-1.5 rounded transition ${
                              flow.isActive
                                ? 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20'
                                : 'text-gray-400 hover:bg-gray-100 dark:text-gray-500 dark:hover:bg-gray-700'
                            } ${flow.isDefault ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={flow.isDefault ? 'Fluxo padrão não pode ser desativado' : (flow.isActive ? 'Desativar fluxo' : 'Ativar fluxo')}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setIsNavigating(true);
                              window.location.href = `/flows/${flow.id}/edit`;
                            }}
                            disabled={isNavigating}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded transition disabled:opacity-50"
                            title={flow.isDefault ? "Visualizar fluxo (somente leitura)" : "Editar fluxo"}
                          >
                            {isNavigating ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                            ) : flow.isDefault ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <Edit className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => openDuplicateModal(flow)}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 rounded transition"
                            title="Duplicar fluxo"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(flow)}
                            disabled={flow._count?.dispatches > 0 || flow.isDefault}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                            title={flow.isDefault ? "Fluxo padrão não pode ser deletado" : (flow._count?.dispatches > 0 ? "Fluxo em uso, não pode ser deletado" : "Deletar fluxo")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && selectedFlow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Deletar Fluxo
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Tem certeza que deseja deletar o fluxo <strong>{selectedFlow.name}</strong>?
              Esta ação não pode ser desfeita.
            </p>
            {selectedFlow._count?.dispatches > 0 && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">
                  Este fluxo está sendo usado por {selectedFlow._count.dispatches} disparo(s) e não pode ser deletado.
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedFlow(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={selectedFlow._count?.dispatches > 0}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Modal */}
      {showDuplicateModal && selectedFlow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Duplicar Fluxo
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Duplicando: <strong>{selectedFlow.name}</strong>
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nome do novo fluxo
              </label>
              <input
                type="text"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Nome do fluxo duplicado"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setSelectedFlow(null);
                  setDuplicateName('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleDuplicate}
                disabled={!duplicateName.trim()}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Duplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowsPage;
